import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, ArrowDown, X, ChevronLeft, ChevronRight } from "lucide-react";
import { getPageContent } from "@/lib/pageContent";

/**
 * Paths absolutos a partir da raiz do site (printlivros.com.br/assets/...).
 * Com <base href="/modelo/estatico/">, "../assets/..." virava /modelo/assets/... (404 no servidor).
 */
const REELS_BASE = "/assets/video/reels";
const HERO_DESKTOP = "/assets/video/videos-gerais/videos-desktop/hero-desk-3g.mp4";
/** Mobile bastidores: 480p (~3.7MB) em vez do 8.9MB padrão */
const HERO_MOBILE = "/assets/video/videos-gerais/videos-mobile/hero-mobile-480.mp4";

export const REELS = [
  { id: "01", label: "Revista", sub: "21x29cm" },
  { id: "02", label: "Infantil", sub: "20x20cm" },
  { id: "03", label: "Agendas", sub: "21x29,7cm" },
  { id: "04", label: "Marcadores", sub: "5x10cm" },
  { id: "05", label: "Devocional", sub: "14x21cm" },
  { id: "06", label: "Apostila", sub: "21x29,7cm" },
  { id: "07", label: "Devocional", sub: "16x23cm" },
  { id: "08", label: "Infantil", sub: "14x21cm" },
].map((r) => ({
  ...r,
  poster: `${REELS_BASE}/posters/${r.id}.jpg`,
  srcDesktop: `${REELS_BASE}/web/${r.id}.mp4`,
  srcMobile: `${REELS_BASE}/web-m/${r.id}.mp4`,
}));

const MOBILE_MQ = "(max-width: 1023px)";

function isMobileViewport() {
  if (typeof window === "undefined") return true;
  return window.matchMedia(MOBILE_MQ).matches;
}

function useIsMobileVideo() {
  const [mobile, setMobile] = useState(isMobileViewport);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    mq.addListener?.(onChange);
    return () => {
      mq.removeEventListener?.("change", onChange);
      mq.removeListener?.(onChange);
    };
  }, []);
  return mobile;
}

function reelSrc(reel, isMobile) {
  return isMobile ? reel.srcMobile : reel.srcDesktop;
}

function heroBastidoresSrc(isMobile) {
  return isMobile ? HERO_MOBILE : HERO_DESKTOP;
}

export default function Hero() {
  const page = getPageContent();
  const hero = page?.hero || {};
  const titleTop = hero.titleTop || "Seu livro,";
  const titleMid = hero.titleMid || "impresso com";
  const titleBottom = hero.titleBottom;
  // Páginas de serviço injetam PL_PAGE_CONTENT — aplicar tipografia do brand (sem mudar a copy)
  const isServicePage = Boolean(page);
  const lead =
    hero.lead ||
    "Produção ágil, suporte dedicado e envio para todo o Brasil. Brochura, capa dura ou grampo — do arquivo ao volume encadernado, sem burocracia.";

  const [reelIdx, setReelIdx] = useState(0);
  const [reelLightbox, setReelLightbox] = useState(null); // number | null
  const [heroLightbox, setHeroLightbox] = useState(false);
  const sectionRef = useRef(null);
  const previewRef = useRef(null);
  const isMobile = useIsMobileVideo();

  const active = REELS[reelIdx];
  const anyLightbox = reelLightbox !== null || heroLightbox;

  const openReel = useCallback((index) => setReelLightbox(index), []);
  const openHeroBastidores = useCallback(() => setHeroLightbox(true), []);

  // Desktop: troca a cada 4.2s. Mobile: autoplay + troca a cada 2s.
  useEffect(() => {
    if (anyLightbox) return undefined;
    const ms = isMobile ? 2000 : 4200;
    const t = setInterval(() => setReelIdx((i) => (i + 1) % REELS.length), ms);
    return () => clearInterval(t);
  }, [anyLightbox, isMobile]);

  // Preview: 1 vídeo por vez (web-m no mobile / web no desktop) — play automático
  useEffect(() => {
    if (anyLightbox) return undefined;
    const v = previewRef.current;
    if (!v) return undefined;
    v.load();
    const play = v.play();
    if (play?.catch) play.catch(() => {});
    return undefined;
  }, [reelIdx, isMobile, anyLightbox]);

  const goQuote = () => document.querySelector("#orcamento")?.scrollIntoView({ behavior: "smooth" });

  /* Mobile: zero motion opacity/y — evita tela branca no scroll iOS.
     Reels mobile: autoplay web-m + troca a cada 2s (1 vídeo por vez). Desktop intacto. */
  const titleBlock = isServicePage ? (
    <>
      <span className="block pb-2 text-[10vw] sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.05]">
        <span className="font-display-anton uppercase text-[#1D1D1B]">{titleTop}</span>
        {titleMid ? (
          <>
            {" "}
            <span className="font-display-playfair italic normal-case text-[#1A45D1] font-normal">
              {titleMid}
              <span className="text-[#E6332A]">.</span>
            </span>
          </>
        ) : null}
      </span>
      {titleBottom ? (
        <span className="block pt-1 font-garamond italic text-[6.5vw] sm:text-2xl lg:text-3xl text-[#666666] normal-case font-normal tracking-normal">
          {titleBottom}
        </span>
      ) : null}
    </>
  ) : (
    <>
      <span className="font-display-anton block text-[13vw] sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight uppercase pb-1">{titleTop}</span>
      <span className="font-garamond italic block text-[8.5vw] sm:text-4xl lg:text-5xl text-neutral-600 py-1">{titleMid}</span>
      <span className="block text-[11vw] sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight uppercase pb-2">
        {titleBottom || (
          <>alma de <span className="font-display-playfair italic normal-case text-blue-700">gráfica<span className="text-red-600">.</span></span></>
        )}
      </span>
    </>
  );

  const badgeBrand = (
    <span className="inline-flex sm:flex sm:w-full sm:justify-center items-center gap-2.5 bg-[#FFDD00] border-2 border-neutral-900/15 rounded-full px-4 py-2 font-bold text-[13px] sm:text-[15px] tracking-[0.14em] text-neutral-900 shadow-[0_6px_20px_-6px_rgba(255,221,0,0.7)]">
      <svg width="22" height="16" viewBox="0 0 24 17" className="rounded-[3px] shadow-sm shrink-0" aria-label="Bandeira do Brasil" role="img">
        <rect width="24" height="17" fill="#009739" />
        <polygon points="12,2.5 21.5,8.5 12,14.5 2.5,8.5" fill="#FEDD00" />
        <circle cx="12" cy="8.5" r="3.2" fill="#012169" />
      </svg>
      A MAIOR GRÁFICA DIGITAL DO BRASIL
    </span>
  );

  const pills = (
    <div className="flex flex-nowrap sm:flex-wrap items-center gap-1.5 sm:gap-2 mb-7">
      <span data-testid="hero-badge-installments" className="border border-neutral-300 text-neutral-700 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full tracking-normal sm:tracking-wide whitespace-nowrap">PARCELE EM ATÉ 12×</span>
      <span data-testid="hero-badge-runs" className="border border-neutral-300 text-neutral-700 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full tracking-normal sm:tracking-wide whitespace-nowrap">GRÁFICA DESDE 2009</span>
      <span data-testid="hero-badge-moq" className="border border-neutral-300 text-neutral-700 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 sm:py-1.5 rounded-full tracking-normal sm:tracking-wide whitespace-nowrap">TIRAGEM A PARTIR DE 1 UN.</span>
    </div>
  );

  const ctas = (
    <div className="flex flex-wrap items-center gap-4">
      <button
        type="button"
        data-testid="hero-cta-start"
        onClick={goQuote}
        className="group bg-neutral-900 text-white font-bold text-base px-7 py-4 rounded-full flex items-center gap-3 hover:bg-blue-700 transition-all duration-300 hover:shadow-[0_16px_40px_-10px_rgba(37,99,235,0.5)] hover:-translate-y-0.5"
      >
        Solicitar orçamento
        <ArrowDown size={18} className="transition-transform duration-300 group-hover:translate-y-1" />
      </button>
      <button
        type="button"
        data-testid="hero-cta-bastidores"
        onClick={openHeroBastidores}
        className="group flex items-center gap-2.5 font-bold text-neutral-800 hover:text-purple-700 transition-colors"
      >
        <span className="w-11 h-11 rounded-full border-2 border-neutral-900 group-hover:border-purple-700 flex items-center justify-center transition-colors">
          <Play size={15} className="ml-0.5" />
        </span>
        Ver bastidores
      </button>
    </div>
  );

  const spotlight = (
    <button
      type="button"
      data-testid="hero-spotlight-image"
      onClick={() => openReel(reelIdx)}
      className="spotlight-frame pl-hero-reel-main relative w-full aspect-[4/5] max-w-md mx-auto rounded-[14px] overflow-hidden block bg-neutral-900"
      aria-label={`Abrir vídeo: ${active.label}`}
    >
      {isMobile ? (
        <video
          key={`mob-${active.id}`}
          ref={previewRef}
          className="absolute inset-0 w-full h-full object-cover"
          poster={active.poster}
          src={reelSrc(active, true)}
          muted
          playsInline
          loop
          autoPlay
          preload="auto"
          aria-label={active.label}
        />
      ) : (
        <video
          key={`desk-${active.id}`}
          ref={previewRef}
          className="absolute inset-0 w-full h-full object-cover"
          poster={active.poster}
          src={reelSrc(active, false)}
          muted
          playsInline
          loop
          autoPlay
          preload="metadata"
          aria-label={active.label}
        />
      )}
      <span className="absolute bottom-4 left-4 z-[2] glass rounded-full px-4 py-1.5 text-xs font-bold text-white lg:text-neutral-800">
        {active.label}
        {active.sub ? ` · ${active.sub}` : ""}
      </span>
      <span className={`pl-hero-reel-play absolute z-[2] rounded-full flex items-center justify-center ${isMobile ? "w-12 h-12 bg-black/55" : "top-4 right-4 w-10 h-10 bg-white/90"}`}>
        <Play size={isMobile ? 18 : 14} className={`ml-0.5 ${isMobile ? "text-white" : "text-neutral-900"}`} />
      </span>
    </button>
  );

  const coverflow = (
    <div
      className={`flex justify-center items-center gap-1.5 sm:gap-2.5 sm:items-end mt-6 w-full max-w-md mx-auto pl-hero-reels-coverflow ${isMobile ? "overflow-visible px-1" : "overflow-hidden"}`}
      data-testid="hero-reels-coverflow"
    >
      {REELS.map((reel, i) => (
        <button
          key={reel.id}
          type="button"
          data-testid={`hero-reel-${i}`}
          onClick={() => { setReelIdx(i); openReel(i); }}
          onMouseEnter={() => { if (!isMobile) setReelIdx(i); }}
          className={`relative w-[11%] max-w-[3.25rem] aspect-[9/16] rounded-lg bg-neutral-900 transition-transform duration-300 shrink ${isMobile ? "overflow-hidden" : "overflow-hidden"} ${i === reelIdx ? "scale-105 sm:scale-110 ring-2 ring-blue-600 shadow-lg z-[1]" : "opacity-70"}`}
        >
          <img
            src={reel.poster}
            alt={reel.label}
            className={`w-full h-full ${isMobile ? "object-contain" : "object-cover"}`}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            width="180"
            height="320"
          />
        </button>
      ))}
    </div>
  );

  return (
    <section
      id="inicio"
      ref={sectionRef}
      className="relative min-h-[100svh] flex items-center pt-24 pb-12 sm:pt-28 sm:pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      {!isMobile && (
        <>
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "linear-gradient(#171717 1px, transparent 1px), linear-gradient(90deg, #171717 1px, transparent 1px)", backgroundSize: "72px 72px" }} />
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-purple-600/10 blur-3xl pointer-events-none" />
        </>
      )}

      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center w-full">
        <div>
          <div className="sm:inline-flex sm:flex-col sm:items-stretch">
            <p className="mb-4" data-testid="hero-tagline">{badgeBrand}</p>
            {pills}
          </div>
          <h1 className="leading-[0.95] mb-7">{titleBlock}</h1>
          <p className="font-garamond italic text-lg sm:text-xl text-neutral-600 max-w-md mb-9" data-testid="hero-lead">{lead}</p>
          {ctas}
        </div>

        <div className="relative">
          {spotlight}
          {coverflow}
        </div>
      </div>

      {reelLightbox !== null && (
        <ReelLightbox
          index={reelLightbox}
          isMobile={isMobile}
          onClose={() => setReelLightbox(null)}
          onNav={setReelLightbox}
        />
      )}
      {heroLightbox && (
        <HeroBastidoresLightbox
          isMobile={isMobileViewport()}
          onClose={() => setHeroLightbox(false)}
        />
      )}
    </section>
  );
}

function HeroBastidoresLightbox({ isMobile, onClose }) {
  const videoRef = useRef(null);
  const src = heroBastidoresSrc(isMobile);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => { document.body.style.overflow = ""; window.__lenis?.start(); };
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    v.load();
    const play = v.play();
    if (play?.catch) play.catch(() => {});
    return () => { v.pause(); };
  }, [src]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-neutral-950/92 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      data-testid="hero-bastidores-lightbox"
    >
      <button
        type="button"
        data-testid="bastidores-close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[220] w-12 h-12 rounded-full bg-white text-neutral-900 shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        aria-label="Fechar vídeo"
      >
        <X size={24} strokeWidth={2.5} />
      </button>
      <motion.figure
        initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }}
        className={`${isMobile ? "max-w-md" : "max-w-4xl"} w-full relative`}
        onClick={(e) => e.stopPropagation()}
      >
        <video
          key={isMobile ? "hero-m" : "hero-d"}
          ref={videoRef}
          className="w-full max-h-[80vh] object-contain rounded-[14px] shadow-2xl bg-black"
          src={src}
          controls
          playsInline
          preload="none"
          controlsList="nodownload noplaybackrate"
          data-testid="hero-bastidores-video"
        />
        <figcaption className="text-center text-white/80 font-garamond italic text-lg mt-4">
          Bastidores da gráfica
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}

function ReelLightbox({ index, isMobile, onClose, onNav }) {
  const videoRef = useRef(null);
  const reel = REELS[index];
  const src = reelSrc(reel, isMobile);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => { document.body.style.overflow = ""; window.__lenis?.start(); };
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    v.load();
    const play = v.play();
    if (play?.catch) play.catch(() => {});
    return () => { v.pause(); };
  }, [src, index]);

  const go = useCallback((delta, e) => {
    e?.stopPropagation();
    onNav((index + delta + REELS.length) % REELS.length);
  }, [index, onNav]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-neutral-950/92 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      data-testid="reel-lightbox"
    >
      <button data-testid="lightbox-close" onClick={onClose} className="absolute top-5 right-5 text-white/80 hover:text-white p-2 z-10" aria-label="Fechar">
        <X size={26} />
      </button>
      <button
        data-testid="lightbox-prev"
        className="absolute left-2 sm:left-8 text-white/70 hover:text-white p-2 z-10"
        onClick={(e) => go(-1, e)}
        aria-label="Anterior"
      >
        <ChevronLeft size={34} />
      </button>
      <motion.figure
        key={index}
        initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }}
        className="max-w-lg w-full" onClick={(e) => e.stopPropagation()}
      >
        <video
          key={`${reel.id}-${isMobile ? "m" : "d"}`}
          ref={videoRef}
          className="w-full max-h-[74vh] object-contain rounded-[14px] shadow-2xl bg-black"
          poster={reel.poster}
          src={src}
          controls
          playsInline
          preload="none"
          controlsList="nodownload noplaybackrate"
        />
        <figcaption className="text-center text-white/80 font-garamond italic text-lg mt-4">
          {reel.label}{reel.sub ? ` · ${reel.sub}` : ""} · {index + 1} de {REELS.length}
        </figcaption>
      </motion.figure>
      <button
        data-testid="lightbox-next"
        className="absolute right-2 sm:right-8 text-white/70 hover:text-white p-2 z-10"
        onClick={(e) => go(1, e)}
        aria-label="Próximo"
      >
        <ChevronRight size={34} />
      </button>
    </motion.div>
  );
}
